import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export interface EmployeeDetails {
  id: string;
  profile_id: string;
  organization_id: string;
  employee_code: string | null;
  date_of_birth: string | null;
  gender: string | null;
  blood_group: string | null;
  marital_status: string | null;
  nationality: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  country_code: string | null;
  emergency_contact_name: string | null;
  emergency_contact_relation: string | null;
  emergency_contact_phone: string | null;
  bank_name: string | null;
  /** Bank account number — column is `bank_account` in DB. */
  bank_account: string | null;
  /** IFSC code — column is `ifsc` in DB. */
  ifsc: string | null;
  /** PAN — column is `pan` in DB. */
  pan: string | null;
  /** Aadhaar in masked form (e.g. XXXXXXXX1234) — column is `aadhaar_masked` in DB. */
  aadhaar_masked: string | null;
  /** UAN — column is `uan` in DB. */
  uan: string | null;
  /** PF Account No — column is `pf_number` in DB. */
  pf_number: string | null;
  esi_number: string | null;
  /** Date joined — column is `joining_date` in DB. */
  joining_date: string | null;
  designation: string | null;
  work_location: string | null;
}

export type EmployeeDetailsInput = Omit<EmployeeDetails, "id" | "organization_id">;

export function useEmployeeDetails(profileId: string | null) {
  return useQuery({
    queryKey: ["employee-details", profileId],
    queryFn: async () => {
      if (!profileId) return null;
      const { data, error } = await supabase
        .from("employee_details")
        .select("*")
        .eq("profile_id", profileId)
        .maybeSingle();
      if (error) throw error;
      return data as EmployeeDetails | null;
    },
    enabled: !!profileId,
    // Employee details are effectively static within a session — avoid
    // refetching every time PaySlipDialog (or any other consumer) mounts.
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
  });
}

export function useUpsertEmployeeDetails() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: EmployeeDetailsInput) => {
      if (!user) throw new Error("Not authenticated");
      if (!input.profile_id) throw new Error("Profile ID is required");

      // Validate UAN if provided — allow "Opted out" sentinel for non-EPF employees
      if (input.uan && input.uan !== "Opted out" && !/^\d{12}$/.test(input.uan)) {
        throw new Error("UAN must be exactly 12 digits (or leave blank / enter \"Opted out\" for non-EPF employees)");
      }
      // Validate ESI number if provided
      if (input.esi_number && !/^\d{17}$/.test(input.esi_number)) {
        throw new Error("ESI number must be exactly 17 digits");
      }
      // Validate IFSC if provided
      if (input.ifsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(input.ifsc)) {
        throw new Error("Invalid IFSC format (expected: ABCD0123456)");
      }
      // Aadhaar is stored masked (e.g. XXXXXXXX1234) — accept the masked form
      // or 4/12 raw digits and let the caller mask before submitting.
      if (input.aadhaar_masked && !/^[X\d]{12}$|^\d{4}$/.test(input.aadhaar_masked)) {
        throw new Error("Aadhaar must be 12 chars (X-masked) or last 4 digits");
      }

      // Detect-then-update-or-insert. We avoid the PostgREST upsert path
      // (which targets `ON CONFLICT (profile_id)`) because in production
      // we've hit "no unique or exclusion constraint matching the ON CONFLICT
      // specification" even after the constraint was confirmed in pg_constraint.
      // The explicit two-step pattern works regardless and is symmetric with
      // the bulk-upload backfill.
      const { data: existing, error: lookupError } = await supabase
        .from("employee_details")
        .select("id")
        .eq("profile_id", input.profile_id)
        .maybeSingle();
      if (lookupError) throw lookupError;

      if (existing?.id) {
        const { data, error } = await supabase
          .from("employee_details")
          .update(input as any)
          .eq("id", existing.id)
          .select()
          .single();
        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from("employee_details")
          .insert(input as any)
          .select()
          .single();
        if (error) throw error;
        return data;
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["employee-details", variables.profile_id] });
      toast.success("Employee details saved");
    },
    onError: (err: any) => {
      toast.error("Failed to save: " + err.message);
    },
  });
}
